import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge
} from '@chakra-ui/react';
import { 
  FiBarChart, 
  FiTrendingUp, 
  FiDollarSign, 
  FiUsers, 
  FiEye,
  FiRefreshCw,
  FiCalendar
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import AnalyticsService, { type CreatorDashboardAnalytics } from '../services/analyticsService';
import IconWrapper from '../components/IconWrapper';

const Analytics: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyticsType, setAnalyticsType] = useState<'creator' | 'platform'>('creator');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y' | 'all'>('30d');
  const [creatorAnalytics, setCreatorAnalytics] = useState<CreatorDashboardAnalytics | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (user) {
      loadCreatorAnalytics();
    }
  }, [user, timeRange]);

  const loadCreatorAnalytics = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const analytics = await AnalyticsService.getCreatorDashboardAnalytics(user.uid);
      setCreatorAnalytics(analytics);
    } catch (error) {
      console.error('Error loading creator analytics:', error);
      setError('Failed to load analytics data. Please try again or refresh the page.');
      // Set default analytics to prevent UI from hanging
      setCreatorAnalytics({
        totalViews: 0,
        totalRevenue: 0,
        totalSubscribers: 0,
        totalContent: 0,
        averageRating: 0,
        topPerformingContent: [],
        recentActivity: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!user) {
    return (
      <Container maxW="1200px" py={8}>
        <Center py={12}>
          <Alert status="info" maxW="md">
            <AlertIcon />
            <AlertDescription>
              Please sign in to view your analytics dashboard.
            </AlertDescription>
          </Alert>
        </Center>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Center minH="50vh">
        <Spinner size="xl" color="purple.400" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container maxW="1200px" py={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="1200px" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <HStack justify="space-between" wrap="wrap" gap={4}>
          <VStack align="start" gap={2}>
            <Heading size="lg" color={textColor}>Analytics Dashboard</Heading>
            <Text color={descriptionColor}>
              Track your content performance and audience insights
            </Text>
          </VStack>
          
          <HStack gap={4}>
            <Select
              value={analyticsType}
              onChange={(e) => setAnalyticsType(e.target.value as 'creator' | 'platform')}
              bg={cardBg}
              borderColor={borderColor}
              color={textColor}
            >
              <option value="creator">Creator Analytics</option>
              <option value="platform">Platform Analytics</option>
            </Select>
            
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              bg={cardBg}
              borderColor={borderColor}
              color={textColor}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
              <option value="all">All time</option>
            </Select>
            
            <Button
              leftIcon={<IconWrapper icon={FiRefreshCw} size={16} />}
              onClick={loadCreatorAnalytics}
              colorScheme="purple"
              variant="outline"
            >
              Refresh
            </Button>
          </HStack>
        </HStack>

        {/* Quick Stats */}
        {creatorAnalytics && (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Total Views</StatLabel>
              <StatNumber color="purple.400">{formatNumber(creatorAnalytics.totalViews)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                All time views
              </StatHelpText>
            </Stat>

            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Total Revenue</StatLabel>
              <StatNumber color="green.400">{formatCurrency(creatorAnalytics.totalRevenue)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Lifetime earnings
              </StatHelpText>
            </Stat>

            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Subscribers</StatLabel>
              <StatNumber color="blue.400">{formatNumber(creatorAnalytics.totalSubscribers)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Total followers
              </StatHelpText>
            </Stat>

            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Content Pieces</StatLabel>
              <StatNumber color="orange.400">{creatorAnalytics.totalContent}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Total uploads
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Top Performing Content */}
        {creatorAnalytics && creatorAnalytics.topPerformingContent.length > 0 && (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack justify="space-between" mb={4}>
              <Heading size="md" color={textColor}>Top Performing Content</Heading>
              <Badge colorScheme="purple" variant="subtle">
                {creatorAnalytics.topPerformingContent.length} items
              </Badge>
            </HStack>
            
            <VStack align="stretch" gap={3}>
              {creatorAnalytics.topPerformingContent.slice(0, 5).map((content, index) => (
                <HStack key={content.contentId} justify="space-between" p={4} bg="gray.50" borderRadius="md">
                  <HStack>
                    <Badge colorScheme="purple" variant="solid" borderRadius="full" px={3} py={1}>
                      #{index + 1}
                    </Badge>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="medium" color={textColor}>{content.title}</Text>
                      <Text fontSize="sm" color={descriptionColor}>
                        {formatNumber(content.views)} views • {formatCurrency(content.revenue)} revenue
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack>
                    <Badge colorScheme="green" variant="subtle">
                      {content.rating.toFixed(1)} ⭐
                    </Badge>
                    <Text fontSize="sm" color={descriptionColor}>
                      {formatPercentage((content.views / creatorAnalytics.totalViews) * 100)} of total views
                    </Text>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        {/* Detailed Analytics Dashboard */}
        <AnalyticsDashboard 
          userId={user.uid}
          isCreatorDashboard={analyticsType === 'creator'}
        />
      </VStack>
    </Container>
  );
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export default Analytics;
